import React from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Chip from '@mui/material/Chip';
import { numberFormat } from '../../util/currency-formatter';
import { VENDOR_TYPE } from '../../constants/vendor.contant';
import './vendor.scss';

/**
 * InvoiceDeleteModalContent Component
 *
 * Reusable deletion confirmation content for vendor invoices across vendor views.
 * Encapsulates invoice summary details, return stock / credit note warnings,
 * comma-separated credit note names, and accounting platform notifications.
 *
 * @param {object} props - Component props.
 * @param {object} [props.invoiceData] - Active invoice metadata to display.
 * @param {boolean} [props.hasReturnedStock=false] - Whether stock return / credit notes are linked.
 * @param {Array} [props.linkedCreditNotes=[]] - Associated credit note records.
 * @param {boolean} [props.isPosted=false] - Whether the invoice has been synced/posted to accounting platform.
 * @returns {React.ReactElement} Centralized delete modal content.
 */
export default function InvoiceDeleteModalContent({
  invoiceData = null,
  hasReturnedStock = false,
  linkedCreditNotes = [],
  isPosted = false
}) {
  const isDeleteBlocked = Boolean(invoiceData?.isDeleteBlocked);
  const isCreditNote = Boolean(invoiceData?.isCreditNote);
  const hasCreditNotes = Array.isArray(linkedCreditNotes) && linkedCreditNotes.length > 0;
  const showWarning = Boolean(hasReturnedStock || hasCreditNotes || isCreditNote || isDeleteBlocked || invoiceData?.hasOverReceivedWarning);
  const creditNoteNames = hasCreditNotes
    ? Array.from(new Set(linkedCreditNotes.map((cn) => cn.invoiceNo).filter(Boolean)))
    : [];

  const returnedItems = Array.isArray(invoiceData?.returnedItems) ? invoiceData.returnedItems : [];
  const totalRevertedQty = invoiceData?.totalRevertedQty !== undefined && invoiceData?.totalRevertedQty !== null
    ? invoiceData.totalRevertedQty
    : returnedItems.reduce((acc, it) => acc + Number(it?.qty || 0), 0);

  const isSubContractor =
    invoiceData?.vendorType === VENDOR_TYPE?.SUB_CONTRACTOR;

  const invoiceAmount = Number(invoiceData?.amountPaid ?? 0);

  return (
    <div className="text-center pt-0 px-2 pb-1">
      {!showWarning && (
        <p className="mb-2">Are you sure you want to delete this {isCreditNote ? 'credit note' : 'invoice'}?</p>
      )}

      {invoiceData && (
        <div className="bg-light border rounded p-3 mb-3 text-start">
          <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
            <span>Invoice No</span>
            <strong>{invoiceData.invoiceNo || '-'}</strong>
          </div>
          <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
            <span>Vendor</span>
            <strong>{invoiceData.vendorName || '-'}</strong>
          </div>
          <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
            <span>{isSubContractor ? 'WO Number' : 'PO Number'}</span>
            <strong>{invoiceData.fId || invoiceData.poNumber || '-'}</strong>
          </div>
          <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
            <span>Project/Job</span>
            <strong>{invoiceData.projectName || '-'}</strong>
          </div>
          <div className="d-flex justify-content-between gap-3 py-1">
            <span>Amount</span>
            <strong>{numberFormat(invoiceAmount)}</strong>
          </div>
        </div>
      )}

      {showWarning && (
        <div className="alert alert-warning text-start p-3 mb-3 invoice-delete-warning-box">
          <div className="d-flex align-items-center gap-1 mb-2">
            <WarningAmberIcon fontSize="small" color={isDeleteBlocked ? 'error' : 'warning'} />
            <strong className="text-dark small">
              {isDeleteBlocked
                ? 'Direct Deletion Restricted'
                : isCreditNote
                  ? 'Return stock associated with this credit note'
                  : 'Stock already returned on this invoice'}
            </strong>
          </div>
          {isDeleteBlocked ? (
            <>
              <p className="small mb-2 text-dark">
                <strong className="text-danger">Action Blocked:</strong> {isCreditNote
                  ? `Deleting this credit note is not allowed because subsequent invoice(s) have already been received for this material on purchase order ${invoiceData?.poNumber || ''}.`
                  : 'Deleting this invoice would cause budget over-invoicing on this purchase order because subsequent replacement invoice(s) have already been issued.'}
              </p>
              {isCreditNote ? (
                <div className="blocked-info-card">
                  <div className="info-row">
                    <span>Purchase Order:</span>
                    <strong>{invoiceData?.poNumber || '-'}</strong>
                  </div>
                  {Array.isArray(invoiceData?.returnedItems) && invoiceData.returnedItems.length > 0 && (
                    <div className="material-section">
                      <span className="section-label">Returned Material(s):</span>
                      {invoiceData.returnedItems.map((item, idx) => (
                        <div key={idx} className="material-name">
                          • {item.material} <span className="material-qty">(Qty: {item.qty})</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {Array.isArray(invoiceData?.replacementInvoices) && invoiceData.replacementInvoices.length > 0 && (
                    <div className="subsequent-section">
                      <span className="section-label">Subsequent Invoice(s) on PO:</span>

                      {/* Web Viewport: Table */}
                      <div className="subsequent-invoices-table-view mt-1">
                        <div className="table-responsive border rounded bg-white">
                          <table className="table table-sm table-bordered mb-0 subsequent-table text-start align-middle">
                            <thead>
                              <tr>
                                <th>Subsequent Invoice</th>
                                <th>Invoiced Material</th>
                                <th className="col-qty">Qty</th>
                                <th className="col-amount">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {invoiceData.replacementInvoices.map((inv) => {
                                const items = Array.isArray(inv.items) && inv.items.length > 0 ? inv.items : [{ material: '-', qty: '-' }];
                                return items.map((it, itemIdx) => (
                                  <tr key={`${inv.id || inv.invoiceNo}-${itemIdx}`}>
                                    {itemIdx === 0 && (
                                      <td rowSpan={items.length} className="fw-bold text-dark text-break">
                                        {inv.invoiceNo}
                                      </td>
                                    )}
                                    <td className="text-dark text-break">{it.material || it.item || it.description || it.name || '-'}</td>
                                    <td className="col-qty fw-bold text-dark">{it.qty !== undefined && it.qty !== null ? it.qty : '-'}</td>
                                    {itemIdx === 0 && (
                                      <td rowSpan={items.length} className="col-amount fw-semibold text-dark">
                                        {numberFormat(inv.amount || 0)}
                                      </td>
                                    )}
                                  </tr>
                                ));
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Mobile Viewport: Grouped Cards */}
                      <div className="subsequent-invoices-cards-view mt-1">
                        {invoiceData.replacementInvoices.map((inv) => (
                          <div key={inv.id || inv.invoiceNo} className="subsequent-invoice-card">
                            <div className="subsequent-invoice-card-header">
                              <span className="invoice-num">{inv.invoiceNo}</span>
                              <span className="invoice-amt">{numberFormat(inv.amount || 0)}</span>
                            </div>
                            <div className="subsequent-invoice-card-items">
                              {Array.isArray(inv.items) && inv.items.length > 0 ? (
                                inv.items.map((it, idx) => (
                                  <div key={idx} className="subsequent-card-item">
                                    • {it.material || it.item || it.description || it.name || '-'} <span className="item-qty">(Qty: {it.qty !== undefined && it.qty !== null ? it.qty : '-'})</span>
                                  </div>
                                ))
                              ) : (
                                <div className="subsequent-card-item">-</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white border rounded p-2 mb-2">
                  <div className="d-flex justify-content-between gap-3 py-1 border-bottom small">
                    <span>Purchase Order Total:</span>
                    <strong>{numberFormat(invoiceData?.poTotal || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between gap-3 py-1 border-bottom small">
                    <span>Other Active Invoices:</span>
                    <strong>{numberFormat(invoiceData?.otherActiveInvoicesTotal || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between gap-3 py-1 border-bottom small">
                    <span>Potential Invoiced Total:</span>
                    <strong className="text-danger">{numberFormat(invoiceData?.potentialOverInvoiceTotal || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between gap-3 py-1 small">
                    <span>Exceeds Budget By:</span>
                    <strong className="text-danger">+{numberFormat(invoiceData?.excessAmount || 0)}</strong>
                  </div>
                </div>
              )}
              {Array.isArray(invoiceData?.replacementInvoices) && invoiceData.replacementInvoices.length > 0 && !isCreditNote && (
                <div className="border-top pt-2 mt-2">
                  <strong className="text-dark small d-block mb-1">Replacement Invoice(s) on PO:</strong>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {invoiceData.replacementInvoices.map((inv) => (
                      <Chip
                        key={inv.id || inv.invoiceNo}
                        size="small"
                        label={`${inv.invoiceNo} (${numberFormat(inv.amount || 0)})`}
                        className="credit-note-chip"
                      />
                    ))}
                  </div>
                </div>
              )}
              <p className="small mb-0 text-dark mt-2">
                {isCreditNote
                  ? 'To delete this credit note, please delete or adjust the subsequent invoice(s) first.'
                  : 'To delete this invoice, please delete or adjust the replacement invoice(s) first.'}
              </p>
            </>
          ) : isCreditNote ? (
            <>
              <p className="small mb-1 text-dark">
                Deleting this credit note will cancel the return transaction.
              </p>
              <p className="small mb-2 text-dark">
                <strong>Confirming will:</strong>
              </p>
              <ul className="small text-dark mb-2 ps-3">
                <li className="mb-1">
                  Permanently <strong>delete this credit note</strong>.
                </li>
                <li>
                  Revert returned item quantities back to received stock.
                </li>
              </ul>
              {invoiceData?.originalInvoiceNo && (
                <div className="border-top pt-2 mt-2 small text-dark">
                  <span>Original Invoice: </span>
                  <strong>{invoiceData.originalInvoiceNo}</strong>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="small mb-1 text-dark">
                Stock has already been returned on this invoice.
              </p>
              <p className="small mb-2 text-dark">
                <strong>Confirming will:</strong>
              </p>
              <ul className="small text-dark mb-2 ps-3">
                <li className="mb-1">
                  Permanently <strong>delete associated return stock transaction and credit note(s)</strong>.
                </li>
                <li>
                  Revert returned item quantities back to received stock.
                </li>
              </ul>
              {creditNoteNames.length > 0 && (
                <div className="border-top pt-2 mt-2">
                  <strong className="text-dark small d-block mb-1">Associated Credit Note(s):</strong>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {creditNoteNames.map((cn) => (
                      <Chip
                        key={cn}
                        size="small"
                        label={cn}
                        className="credit-note-chip"
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {!isDeleteBlocked && returnedItems.length > 0 && (
            <div className="border-top pt-2 mt-2">
              <strong className="text-dark small d-block mb-1">Returned Items to be Reverted:</strong>
              <div className="table-responsive border rounded bg-white mt-1 mb-1">
                <table className="table table-sm table-bordered mb-0 small text-start align-middle invoice-return-items-table">
                  <thead className="table-light">
                    <tr>
                      <th className="py-1 px-2">Task</th>
                      <th className="py-1 px-2">Material</th>
                      <th className="py-1 px-2 text-center col-qty">Qty</th>
                      <th className="py-1 px-2">Credit No/Invoice No.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returnedItems.map((item, idx) => (
                      <tr key={`${item.id || 'item'}-${item.creditNoteNo || idx}-${idx}`}>
                        <td className="py-1 px-2 fw-semibold text-break">{item.taskName || '-'}</td>
                        <td className="py-1 px-2 text-break">{item.material || '-'}</td>
                        <td className="py-1 px-2 text-center fw-bold">{item.qty}</td>
                        <td className="py-1 px-2 text-break">{item.creditNoteNo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="small text-start mt-1 reverted-qty-summary">
                Total Reverted Qty: <strong>{totalRevertedQty} {totalRevertedQty === 1 ? 'unit' : 'units'}</strong>
              </div>
            </div>
          )}
        </div>
      )}

      {isPosted && (
        <div className="alert alert-warning text-start p-2 mb-3">
          <div className="d-flex align-items-center gap-1 mb-1">
            <WarningAmberIcon fontSize="small" color="warning" />
            <strong className="text-dark small">Accounting Platform Warning</strong>
          </div>
          <p className="small mb-0 text-dark">
            This {isCreditNote ? 'credit note' : 'invoice'} has been sent to your accounting platform. Please ensure you also remove it from your accounting platform.
          </p>
        </div>
      )}

      {invoiceData?.hasOverReceivedWarning && Array.isArray(invoiceData?.overReceivedItems) && invoiceData.overReceivedItems.length > 0 && (
        <div className="alert alert-warning text-start p-2 mb-3">
          <div className="d-flex align-items-center gap-1 mb-1">
            <WarningAmberIcon fontSize="small" color="warning" />
            <strong className="text-dark small">Over-Received Stock Warning</strong>
          </div>
          <p className="small mb-1 text-dark">
            After clicking <strong>Confirm & Proceed</strong>, Received Stock quantities may be over the ordered quantity. To correct this, please manually perform a Return Stock on the Purchase Order for any additional quantity reported.
          </p>
          <div className="table-responsive">
            <table className="table table-sm table-bordered small mb-0">
              <thead>
                <tr className="table-light">
                  <th>Material</th>
                  <th className="text-end">Ordered</th>
                  <th className="text-end">Projected Received</th>
                  <th className="text-end">Excess</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.overReceivedItems.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td>{item.material || '-'}</td>
                    <td className="text-end">{item.orderedQty}</td>
                    <td className="text-end">{item.projectedReceivedQty}</td>
                    <td className="text-end text-danger fw-bold">+{item.excessQty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isDeleteBlocked && (
        <p className="text-danger fw-bold mb-0">
          This action cannot be undone.
        </p>
      )}
    </div>
  );
}

export { InvoiceDeleteModalContent as InvoiceDeleteBreakdownTable };
